// src/modules/app-views/toolbox/pages/Init/modals/ToolboxParametersModal/tabs/ToolkitsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import useZustand from 'hooks/useZustand';
import ToolkitFormModal from './ToolkitFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ToolkitsTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedModule = useZustand('module', 'config');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for toolkits - initialize from store, update locally after save
  const [toolkits, setToolkits] = useState(() => {
    const rawToolkits = appService.getModulePreferences({ category: 'toolkits' });
    return normalizeToolkits(rawToolkits);
  });

  // Filter out removed toolkits for display in table
  const visibleToolkits = useMemo(() => toolkits.filter(t => !t.removed), [toolkits]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed toolkits)
      const isDuplicate = toolkits.some(
        t =>
          t.uid !== editingRecord?.uid &&
          !t.removed &&
          t.name.toLowerCase() === formData.name.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedToolkits;

        if (editingRecord) {
          // Editing existing toolkit - keep the same uid and preserve removed flag
          updatedToolkits = toolkits.map(t =>
            t.uid === editingRecord.uid
              ? { ...t, name: formData.name, description: formData.description }
              : t
          );
        } else {
          // Adding new toolkit - generate new uid
          updatedToolkits = [
            ...toolkits,
            {
              uid: generateUidV4(),
              name: formData.name,
              description: formData.description,
            },
          ];
        }

        // Save array directly: toolkits: [...]
        await appService.updateModulePreferences({
          category: 'toolkits',
          data: updatedToolkits,
        });

        // Update local state immediately (don't wait for SSE)
        setToolkits(updatedToolkits);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[ToolkitsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [toolkits, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        const moduleId = storedModule?.id || 'toolbox';

        // Check if toolkit is in use via API
        const isInUse = await appService.checkParameterInUse({
          module: moduleId,
          category: 'toolkits',
          uid: record.uid,
        });

        let updatedToolkits;

        if (isInUse) {
          // Soft delete: mark as removed but keep in array
          updatedToolkits = toolkits.map(t =>
            t.uid === record.uid ? { ...t, removed: true } : t
          );
          appService.message({ flag: 'w', text: 'item_in_use_hidden' });
        } else {
          // Hard delete: remove from array
          updatedToolkits = toolkits.filter(t => t.uid !== record.uid);
          appService.message({ flag: 's', text: 'removed_successfully' });
        }

        // Save array directly: toolkits: [...]
        await appService.updateModulePreferences({
          category: 'toolkits',
          data: updatedToolkits,
        });

        // Update local state immediately (don't wait for SSE)
        setToolkits(updatedToolkits);
      } catch (error) {
        console.error('[ToolkitsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [toolkits, setLoading, storedModule?.id]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('name'),
      dataIndex: 'name',
      width: 160,
      render: text => <span className="font-weight-bold">{text}</span>,
    },
    {
      title: translateX('description'),
      dataIndex: 'description',
      render: text => text || <span className="text-muted">-</span>,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleToolkits}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_toolkit')}
        </Button>
      </Flex>

      <ToolkitFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ToolkitsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeToolkits(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeToolkit);
}

function normalizeToolkit(toolkit) {
  return {
    uid: toolkit.uid,
    name: toolkit.name || '',
    description: toolkit.description || '',
    ...(toolkit.removed && { removed: true }),
  };
}
